import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { t as tRaw } from '@/locales/strings';

// Listens for a waiting service worker (installed but not yet
// activated because older tabs are still using the old cache) and
// surfaces a small bottom-of-screen banner prompting the user to
// reload. On confirm we message the waiting worker with `SKIP_WAITING`
// so it takes over, then reload the page so the new bundle runs.
//
// This runs alongside the vanilla-side registration in src/main.js —
// we only read from navigator.serviceWorker, never call register()
// ourselves, so there is no risk of a double registration race.

export default function ServiceWorkerUpdater() {
  const { lang } = useApp();
  const [waiting, setWaiting] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;

    async function watch() {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg || cancelled) return;

        // A waiting worker is already queued from a previous tab
        // install — surface it immediately on mount.
        if (reg.waiting) setWaiting(reg.waiting);

        // Future updates: poll updatefound + track the installing
        // worker until it flips to 'installed' behind an active
        // controller (which is the "update ready" moment).
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              setWaiting(installing);
            }
          });
        });
      } catch (err) {
        console.warn('[SWUpdater] failed to inspect registration', err);
      }
    }

    watch();
    return () => { cancelled = true; };
  }, []);

  if (!waiting || dismissed) return null;

  function applyUpdate() {
    try { waiting.postMessage({ type: 'SKIP_WAITING' }); } catch {}
    // Give the worker a tick to activate, then reload. A single-shot
    // flag ensures we only reload once even if both triggers fire —
    // controllerchange and the 2s Safari fallback can race on slow
    // networks.
    let reloaded = false;
    let fallbackTimer;
    const reload = () => {
      if (reloaded) return;
      reloaded = true;
      clearTimeout(fallbackTimer);
      navigator.serviceWorker.removeEventListener('controllerchange', reload);
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', reload);
    fallbackTimer = setTimeout(reload, 2000);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 12, right: 12,
        bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        zIndex: 39000,
        background: 'var(--surface, #FFFFFF)',
        border: '1px solid var(--border, #E8DDD4)',
        borderRadius: 14,
        padding: '12px 14px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
        maxWidth: 460,
        margin: '0 auto',
      }}
    >
      <div style={{
        flex: 1,
        fontSize: 13,
        color: 'var(--dark-brown, #5A3E2B)',
        lineHeight: 1.4,
      }}>
        {tRaw('sw_update_body', lang)}
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        style={{
          padding: '8px 10px',
          background: 'transparent',
          border: 'none',
          color: 'var(--warm-brown, #8B7355)',
          fontFamily: 'inherit',
          fontSize: 12,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {tRaw('sw_update_later', lang)}
      </button>
      <button
        type="button"
        onClick={applyUpdate}
        style={{
          padding: '8px 16px',
          background: 'var(--primary, #A8003F)',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          fontFamily: 'inherit',
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {tRaw('sw_update_reload', lang)}
      </button>
    </div>
  );
}
